import { z } from 'zod/v4'

import {
  AdminJobFamilySchema,
  AdminJobSchema,
  AdminJobsListQuerySchema,
  AdminJobStatusSchema,
} from '../../contracts/admin/admin-jobs.contracts.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Trabajos»: one page of the platform jobs and the counts of the strip in one answer. The row is `AdminJobSchema`
 * (the wire shape `toAdminJob` already builds, field by field); the counts look only at organisation and period, like
 * the strip of the page. The type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-jobs-screen.type-assertions.ts` tie both in both directions.
 */

/** The counts of the strip (= `AdminJobsSummary`). */
export const adminJobsSummarySchema = z.strictObject({
  total: z.number(),
  byStatus: z.record(AdminJobStatusSchema, z.number()),
  unknownStatus: z.number(),
  byFamily: z.record(
    AdminJobFamilySchema,
    z.strictObject({ total: z.number(), failed: z.number(), retryableFailed: z.number() }),
  ),
  retryableFailed: z.number(),
})

export const adminJobsPageSchema = z.strictObject({
  items: z.array(AdminJobSchema),
  pagination: adminPaginationSchema,
})

/**
 * The names are the ones of `GET /api/admin/system/jobs`. `status` is ONE value (the page only sends one); `retryable`
 * travels as text and the composition turns it into a boolean; `tenantId` is a `guid` (the seeded organisations carry
 * no version nibble). There is no `sortBy` / `sortOrder`: the page sends none and the composition uses `createdAt` /
 * `desc`.
 */
export const adminJobsQuerySchema = z.strictObject({
  // The filters the list route already validates, taken from ITS schema: one definition of each.
  family: AdminJobsListQuerySchema.shape.family,
  jobType: AdminJobsListQuerySchema.shape.jobType,
  q: AdminJobsListQuerySchema.shape.q,
  queue: AdminJobsListQuerySchema.shape.queue,
  from: AdminJobsListQuerySchema.shape.from,
  to: AdminJobsListQuerySchema.shape.to,
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(25),
  status: AdminJobStatusSchema.optional(),
  tenantId: z.guid().optional(),
  retryable: z.enum(['true', 'false']).optional(),
})

export const adminJobsScreen = defineScreen({
  id: 'admin.jobs',
  path: '/api/screens/admin/jobs',
  access: 'super_admin',
  query: adminJobsQuerySchema,
  response: z.strictObject({
    page: screenSection(adminJobsPageSchema),
    summary: screenSection(adminJobsSummarySchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.jobs'],
  budget: { requests: 1, statements: 6, bytes: 80_000, p95Ms: 400 },
  summary: 'Jobs: one page of the platform jobs and the counts of the strip',
})

export type AdminJobsQuery = ScreenQuery<typeof adminJobsScreen>
export type AdminJobsData = ScreenData<typeof adminJobsScreen>
